import { useState } from 'react';
import { Badge, Group, Select } from '@mantine/core';
import { IconBoxOff } from '@tabler/icons-react';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { useProducts } from '../api';
import type { ProductRow } from '../types';

const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Active only' },
  { value: 'false', label: 'Inactive only' },
];

/** Register — products (read view) (BUILD_SPEC.md §11 "Registers ... products").
 * Products are owned by the config module (G13); this is a read-only browse
 * screen, no CRUD (product creation stays in Settings). */
export function ProductsRegister() {
  const [activeFilter, setActiveFilter] = useState<string | null>('true');
  const isActive = activeFilter === null ? undefined : activeFilter === 'true';

  const query = useProducts({ isActive });
  const rows = query.data?.items ?? [];

  const columns: ReportColumn<ProductRow>[] = [
    { key: 'sku', header: 'SKU', render: (r) => r.sku },
    { key: 'name', header: 'Name', render: (r) => r.name },
    { key: 'type', header: 'Type', render: (r) => <Badge variant="light">{r.type}</Badge> },
    { key: 'status', header: 'Status', render: (r) => <Badge color={r.isActive ? 'green' : 'gray'} variant="light">{r.isActive ? 'Active' : 'Inactive'}</Badge> },
  ];

  return (
    <ReportPage title="Products" description="Read-only register of every product master (create/edit lives in Settings).">
      <Group gap="md" wrap="wrap" align="flex-end">
        <Select
          label="Status"
          data={ACTIVE_OPTIONS}
          value={activeFilter}
          onChange={setActiveFilter}
          clearable
          placeholder="All"
          w={200}
          comboboxProps={{ withinPortal: true }}
        />
      </Group>

      <ReportTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.id}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage="Failed to load products."
        emptyMessage="No products found"
        emptyHint="Try switching between active and inactive."
        emptyIcon={IconBoxOff}
      />
    </ReportPage>
  );
}
